import { ExternalLink } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import { SIDEBAR_MENU_ITEMS } from '@/constants/ui/sidebar';
import { useGetLinks } from '@/hooks/useGetLinks';
import { useLinkStore } from '@/stores/useLinkStore';

import { CustomOrganizationSwitcher } from './OrganizationSwitcher';

export const AppSidebar = () => {
  // custom hooks
  const location = useLocation();
  const { setLinks, setLoading, setError, getVisibleLinks } = useLinkStore();

  // hooks
  const {
    result: linksResult,
    isLoading: isLinksLoading,
    isError: isLinksError,
    isSuccess: isLinksSuccess,
  } = useGetLinks();

  // effects
  useEffect(() => {
    setLoading(isLinksLoading);
  }, [isLinksLoading, setLoading]);

  useEffect(() => {
    if (isLinksError) {
      setError('Failed to load links');
    }
  }, [isLinksError, setError]);

  useEffect(() => {
    if (isLinksSuccess && linksResult?.data) {
      setLinks(linksResult.data);
    }
  }, [isLinksSuccess, linksResult, setLinks]);

  // render functions
  const renderMenuItem = (item: (typeof SIDEBAR_MENU_ITEMS)[0]) => {
    const className = `sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}-button`;

    return (
      <SidebarMenuButton
        asChild
        isActive={location.pathname === item.path}
        className={className}
      >
        <Link to={item.path} className="flex items-center justify-between">
          <div className="flex items-center gap-3 overflow-hidden">
            <item.icon size={20} />
            <span className="truncate">{item.label}</span>
          </div>
        </Link>
      </SidebarMenuButton>
    );
  };

  // Get visible links (filtered for consultant dashboard)
  const visibleLinks = getVisibleLinks();

  return (
    <Sidebar className="z-20">
      <div className="flex pb-3">
        <CustomOrganizationSwitcher />
      </div>
      <SidebarSeparator className="mb-3" />
      <SidebarContent className="overflow-hidden">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {SIDEBAR_MENU_ITEMS.map((item) => (
                <SidebarMenuItem key={item.label}>
                  {renderMenuItem(item)}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {visibleLinks.length > 0 && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupLabel>Quick Links</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleLinks.map((link) => (
                    <SidebarMenuItem key={link.id}>
                      <SidebarMenuButton asChild>
                        <a
                          href={
                            link.link?.startsWith('http')
                              ? link.link
                              : `https://${link.link}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2"
                        >
                          <ExternalLink className="size-4" />
                          <span>{link.title}</span>
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
        <SidebarSeparator />
      </SidebarContent>
    </Sidebar>
  );
};
